import Vue from 'vue'
import VueI18n from 'vue-i18n'

import ElementLocale from 'element-ui/lib/locale/lang/en'

import Cookie from './cookie'
import DomainName, { DomainForSubdomainName } from './domain'

/**
 * @module translate
 * @category client
 */

const LANGUAGES_URL = `${window.location.protocol}//languages.${DomainName}`
const LANGUAGES_SUBDOMAIN_URL = `${window.location.protocol}//languages.${DomainForSubdomainName}`

export const TranslateEventBus = new Vue()

const DEFAULT_LANGUAGE = /** @type {const} */('en')

const messages = { en: {} }

Vue.use(VueI18n)

export const i18n = new VueI18n({
  locale: DEFAULT_LANGUAGE, // set locale
  fallbackLocale: DEFAULT_LANGUAGE,
  messages, // set locale messages
  silentTranslationWarn: true // don't show warnings about missing translation
})

const loadedLanguages = new Set()

const languages = new Map()

let langsConfig = null

/**
 * Loads translation data for a specific language from the server.
 *
 * @memberof module:translate
 * @async
 * @param {string} languageName - The language code to load translations for.
 * @param {string} version - The version of translations to load.
 * @param {boolean} [useSubdomain=false] - Whether to use subdomain for the request.
 * @returns {Promise<Object>} The loaded translation data with config and translations.
 */
const loadTranslations = async (languageName, version, useSubdomain = false) => {
  const base = useSubdomain ? LANGUAGES_SUBDOMAIN_URL : LANGUAGES_URL
  const responses = await Promise.all([
    fetch(
      `${base}/site/${languageName}.json?${version}`
    ),
    fetch(
  `${base}/missions/${languageName}.json?${version}`
    ),
    fetch(
  `${base}/account/${languageName}.json?${version}`
    )
  ])
  const results = []
  for (const response of responses) {
    results.push(response.json())
  }
  const siteData = {
    config: {},
    translations: {}
  };

  (await Promise.all(results)).forEach(data => {
    if (data.config) {
      siteData.config = data.config
    }
    Object.assign(siteData.translations, data.translation || data.translations || data)
  })

  // eslint-disable-next-line no-restricted-syntax

  return siteData
}

/**
 * Gets a URL parameter value by name.
 *
 * @memberof module:translate
 * @param {string} name - The parameter name to retrieve.
 * @returns {string} The parameter value or empty string if not found.
 */
const getParameterByName = (name) => {
  const url = window.location.href
  // eslint-disable-next-line no-param-reassign
  name = name.replace(/[[\]]/g, '\\$&')
  const regex = new RegExp(`[?&]${name}(=([^&#]*)|&|#|$)`)
  const results = regex.exec(url)
  if (!results) return ''
  if (!results[2]) return ''
  const result = decodeURIComponent(results[2]).split('&')
  return result[0].replace(/\+/g, ' ')
}

/**
 * Gets the operating system language.
 *
 * @memberof module:translate
 * @returns {string} The OS language code or default language if not supported.
 */
const getOSlang = () => {
  const osLang = (navigator.language || navigator.userLanguage)
    .toLowerCase()
    .split('-')[0]
  if (languages.has(osLang)) return osLang
  return DEFAULT_LANGUAGE
}

/**
 * Gets the current language from URL parameter, cookie, or OS language.
 *
 * @memberof module:translate
 * @returns {string} The current language code.
 */
const getLanguage = () => {
  let lang = getParameterByName('lang')
  if (!lang) {
    lang = Cookie.getCookie('lang')
    if (!lang) lang = getOSlang()
  }
  lang = lang.toLowerCase()
  return lang || DEFAULT_LANGUAGE
}

/**
 * Gets the text direction for a language.
 *
 * @memberof module:translate
 * @param {string} lang - The language code.
 * @returns {string} The text direction ('ltr' or 'rtl').
 */
const getDirection = (lang) => {
  if (!languages.has(lang)) return 'ltr'

  return languages.get(lang).direction
}

/**
 * Sets the i18n language and updates the HTML document attributes.
 *
 * @memberof module:translate
 * @param {string} lang - The language code to set.
 * @returns {string} The language code that was set.
 */
const setI18nLanguage = (lang) => {
  i18n.locale = lang

  // axios.defaults.headers.common["Accept-Language"] = lang;

  document.querySelector('html').setAttribute('lang', lang)

  const dir = getDirection(lang)

  document.querySelector('html').setAttribute('dir', dir)

  return lang
}

/**
 * Adds a cookie with specified parameters.
 *
 * @memberof module:translate
 * @param {string} name - The cookie name.
 * @param {string} value - The cookie value.
 * @param {number} expirationDays - Days until expiration.
 * @param {string} path - The cookie path.
 * @param {string} domain - The cookie domain.
 */
const addCookie = (name, value, expirationDays, path, domain) => {
  let date = new Date()
  date.setTime(+date + expirationDays * 86400000)
  date = date.toGMTString()
  document.cookie = `${name}=${value}; expires=${date}; path=${path}; domain=${domain}`
}

/**
 * Loads multiple languages asynchronously.
 *
 * @memberof module:translate
 * @async
 * @param {string[]} [languagesToLoad=[DEFAULT_LANGUAGE]] - Array of language codes to load.
 * @param {boolean} [useSubdomain=false] - Whether to use subdomain for requests.
 */
const getLanguagesAsync = async (languagesToLoad = [DEFAULT_LANGUAGE], useSubdomain = false) => {
  const timeNow = new Date().getTime()

  const configResponse = await fetch(
        `${useSubdomain ? LANGUAGES_SUBDOMAIN_URL : LANGUAGES_URL}/config/all.json?${timeNow}`
  )
  const configData = await configResponse.json()

  const promises = []

  const currentLanguages = [...new Set(languagesToLoad)]

  langsConfig = {
    ...configData,
    languages: configData.languages.filter(({ supported }) => !!supported)
  }

  configData.languages.forEach((lang) => {
    if (!lang.supported || !currentLanguages.includes(lang.code)) return

    promises.push(
      new Promise(async (resolve) => {
        const langData = await loadTranslations(
          lang.code,
          configData.version,
          useSubdomain
        )
        // TODO DIMA: Restore this line after finishing updating local translations
        // const langData = await langResponse.json();

        const langConfig = langData.config
        const langTranslations = langData.translations

        langConfig.code = lang.code

        i18n.setLocaleMessage(lang.code, langTranslations)
        loadedLanguages.add(lang.code)

        languages.set(lang.code, langConfig)

        resolve()
      })
    )
  })

  await Promise.all(promises)
}

/**
 * Gets the list of available languages.
 *
 * @memberof module:translate
 * @returns {Array<Object>} Array of language objects with label and value properties.
 */
export const getLanguages = () => {
  const returnData = langsConfig.languages.map((lang) => ({
    label: lang.name,
    value: lang.code
  }))

  return returnData
}

/**
 * Gets language configuration by language code.
 *
 * @memberof module:translate
 * @param {string} lang - The language code.
 * @returns {Object} The language configuration object.
 */
export const getLanguageByCode = (lang) => {
  if (languages.has(lang)) return languages.get(lang)
  return languages.get(DEFAULT_LANGUAGE)
}

const haveLangInConfig = (lang) => languages.has(lang)

/**
 * Loads a language asynchronously and sets it as the current language.
 *
 * @memberof module:translate
 * @async
 * @param {boolean} [useSubdomain=false] - Whether to use subdomain for requests.
 * @returns {Promise<string>} Promise that resolves with the language code.
 */
export const loadLanguageAsync = async (useSubdomain = false) => {
  const lang = getLanguage()

  if (!languages.size || !haveLangInConfig(lang)) {
    await getLanguagesAsync([DEFAULT_LANGUAGE, lang], useSubdomain)
  }

  // If the same language
  if (i18n.locale === lang) return Promise.resolve(setI18nLanguage(lang))

  // If the language was already loaded
  if (loadedLanguages.has(lang)) {
    return Promise.resolve(setI18nLanguage(lang))
  }

  const configResponse = await fetch(
        `${useSubdomain ? LANGUAGES_SUBDOMAIN_URL : LANGUAGES_URL}/config/all.json?${new Date().getTime()}`
  )
  const configData = await configResponse.json()

  const languageData = await loadTranslations(lang, configData.version, useSubdomain)

  i18n.setLocaleMessage(lang, languageData.translations)
  loadedLanguages.add(lang)
  return setI18nLanguage(lang)
}

/**
 * Sets the language and saves it to cookies.
 *
 * @memberof module:translate
 * @async
 * @param {string} lang - The language code to set.
 * @param {boolean} [useSubdomain=false] - Whether to use subdomain for requests.
 */
export const setLanguage = async (lang, useSubdomain = false) => {
  // eslint-disable-next-line no-param-reassign
  if (!lang) lang = getOSlang()
  const mainDomainSpl = window.location.hostname.split('.')
  if (useSubdomain) {
    const cookieDomain = `.${mainDomainSpl[mainDomainSpl.length - 2]}.${mainDomainSpl[mainDomainSpl.length - 1]}`
    Cookie.removeCookie('lang', window.location.hostname.replace(/^[a-z1-9]+/, ''))
    addCookie('lang', lang, 30, '/', cookieDomain)
  } else {
    addCookie('lang', lang, 30, '/', window.location.hostname.replace(/^[a-z1-9]+/, ''))
  }
  await loadLanguageAsync(useSubdomain)
  TranslateEventBus.$emit('language-set', lang)
  // return lang;
}

export { ElementLocale }
